import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader } from '../components/UI';
import DataFreshnessBanner from '../components/DataFreshnessBanner';
import { CommentButton } from '../components/Comments';
import { api, moneyFull, pct } from '../lib/api';
import { ChevronRight, ChevronDown, Printer } from 'lucide-react';

const EXPENSE_SECTIONS = [
  { key: 'leadership', label: 'Leadership' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'sales', label: 'Sales' },
  { key: 'fulfillment', label: 'Fulfillment' },
  { key: 'overhead', label: 'Overhead' },
];

export default function PnL() {
  const { active, forecastData } = useScenario();
  const [expanded, setExpanded] = useState({});
  const [actuals, setActuals] = useState([]);

  useEffect(() => { api.get('/actuals').then((r) => setActuals(r.data)).catch(() => {}); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!forecastData) return <div className="label-editorial">Loading…</div>;

  // Rolling 9-month view: recent actuals + placeholders for closed months
  // still waiting on QBO + next forecast months, capped at 9 columns total.
  //
  // Doctrine: actuals ALWAYS beat forecast. But if a past calendar month has
  // no actuals row yet (QBO / Indie Financials sheet not refreshed), we no
  // longer just skip it — we insert a "PENDING" column using the current
  // forecast baseline as a placeholder estimate. Advisor Feb 2026: hiding
  // June entirely when it hadn't posted was more confusing than showing an
  // acknowledged-pending column.
  const allForecastMonths = forecastData.forecast.months;
  const closedActuals = actuals.slice(-3);
  const actualLabels = new Set(closedActuals.map((a) => a.month_label));

  // "Latest actual" and "current calendar month" — used to find the gap.
  const nowUtc = new Date();
  const currentMonthStart = new Date(Date.UTC(nowUtc.getUTCFullYear(), nowUtc.getUTCMonth(), 1));
  const latestActualDate = closedActuals.length
    ? new Date(closedActuals[closedActuals.length - 1].date_iso)
    : null;

  // Build pending months between latest actual + 1 and current month exclusive.
  // Each pending column reuses the first forecast row (which represents the
  // current-month baseline) so the numbers are a fair proxy for what June
  // would have looked like at the same assumptions.
  const pendingMonths = [];
  if (latestActualDate && allForecastMonths.length) {
    const cursor = new Date(Date.UTC(latestActualDate.getUTCFullYear(), latestActualDate.getUTCMonth() + 1, 1));
    while (cursor < currentMonthStart) {
      const label = cursor.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }) + ' ' + cursor.getUTCFullYear();
      if (!actualLabels.has(label)) {
        pendingMonths.push({
          label,
          date_iso: cursor.toISOString(),
          // Clone the first forecast row's shape so all downstream getters
          // work unchanged. Numbers are the forecast baseline, clearly
          // marked as "actuals pending" in the header.
          template: allForecastMonths[0],
        });
      }
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
  }

  // Take enough forecast months to fill 9 columns total (actuals + pending + fc).
  const forecastRoomLeft = Math.max(0, 6 - pendingMonths.length);
  const forecastMonths = allForecastMonths
    .filter((m) => !actualLabels.has(m.label))
    .slice(0, forecastRoomLeft + 6 - Math.min(6, forecastRoomLeft))  // still cap at 6 forecast cols
    .slice(0, Math.max(6 - pendingMonths.length, 3));

  // Recompute "totals" for just the 6 visible forecast months (not all 12).
  const totals = forecastMonths.reduce((acc, m) => ({
    revenue: acc.revenue + m.income.total,
    expenses: acc.expenses + m.expenses.total,
    net_income: acc.net_income + m.net_income,
    margin: 0,
  }), { revenue: 0, expenses: 0, net_income: 0, margin: 0 });
  totals.margin = totals.revenue ? totals.net_income / totals.revenue : 0;

  const actualsTotal = closedActuals.reduce(
    (acc, a) => ({ rev: acc.rev + a.revenue, exp: acc.exp + a.expenses, net: acc.net + a.net_income }),
    { rev: 0, exp: 0, net: 0 },
  );

  const months = forecastMonths;

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));
  // Sum a forecast-getter across both forecast + pending months so drill-in
  // line-item totals include the pending estimate.
  const sumLine = (pathFn) =>
    months.reduce((acc, m) => acc + (pathFn(m) || 0), 0)
    + pendingMonths.reduce((acc, p) => acc + (pathFn(p.template) || 0), 0);

  // Build the unified column set: actuals + pending (past-month, awaiting QBO)
  // + forecast + total. Column order stays chronological.
  const allColumns = [
    ...closedActuals.map((a) => ({ kind: 'actual', label: a.month_label, actual: a })),
    ...pendingMonths.map((p) => ({ kind: 'pending', label: p.label, forecast: p.template })),
    ...months.map((m) => ({ kind: 'forecast', label: m.label, forecast: m })),
  ];
  const columnKeys = [...allColumns.map((c) => c.label), 'total'];

  // Totals across the full window (actuals + pending estimates + forecast).
  // Pending months are estimates but we still roll them into the 9-mo total
  // so the sum reflects the whole visible window — flagged in copy below.
  const pendingTotal = pendingMonths.reduce((acc, p) => ({
    rev: acc.rev + p.template.income.total,
    exp: acc.exp + p.template.expenses.total,
    net: acc.net + p.template.net_income,
  }), { rev: 0, exp: 0, net: 0 });
  const grandTotals = {
    revenue: actualsTotal.rev + pendingTotal.rev + totals.revenue,
    expenses: actualsTotal.exp + pendingTotal.exp + totals.expenses,
    net_income: actualsTotal.net + pendingTotal.net + totals.net_income,
  };
  grandTotals.margin = grandTotals.revenue ? grandTotals.net_income / grandTotals.revenue : 0;

  const DASH = <span className="text-indie-ink/30">—</span>;

  // Render a value cell that's either a number, a "—" (no actual line-item),
  // or a passed-through React element. Actuals get an evergreen accent, pending
  // estimates get a muted italic treatment so they read as "estimate".
  const cell = (val, col) => {
    if (val === DASH || val === null || val === undefined) return DASH;
    const formatted = typeof val === 'number' ? moneyFull(val) : val;
    let accent = '';
    if (col?.kind === 'actual') accent = 'text-indie-evergreen';
    else if (col?.kind === 'pending') accent = 'text-indie-cobalt italic opacity-70';
    return <span className={accent}>{formatted}</span>;
  };

  // Returns the correct value for a column (actual, pending, or forecast). For
  // pending columns we fall through to the forecast baseline stored on the
  // column so all line items keep working.
  const lineValue = (col, forecastGetter, actualGetter) => {
    if (col.kind === 'actual') {
      const v = actualGetter ? actualGetter(col.actual) : null;
      return v == null ? DASH : v;
    }
    return forecastGetter(col.forecast);
  };

  // Sum across the visible window for a given line. Includes pending estimates
  // on the forecast side so 9-mo totals stay math-consistent.
  const lineSum = (forecastGetter, actualGetter) => {
    const fcSum = months.reduce((acc, m) => acc + (forecastGetter(m) || 0), 0);
    const pnSum = pendingMonths.reduce((acc, p) => acc + (forecastGetter(p.template) || 0), 0);
    const acSum = actualGetter
      ? closedActuals.reduce((acc, a) => acc + (actualGetter(a) || 0), 0)
      : 0;
    return fcSum + pnSum + acSum;
  };

  const row = (label, getValueForColumn, opts = {}) => (
    <tr className={opts.bold ? 'bg-indie-cream font-medium text-indie-navy' : ''}>
      <td className={`px-3 py-2 ${opts.indent ? 'pl-10' : ''} ${opts.bold ? 'text-indie-navy' : ''}`}>{label}</td>
      {allColumns.map((col, i) => {
        const bg = col.kind === 'actual'
          ? 'bg-indie-evergreen/[0.03]'
          : col.kind === 'pending'
          ? 'bg-indie-cobalt/[0.04]'
          : '';
        return (
          <td key={`${label}-${columnKeys[i]}`} className={`text-right px-3 py-2 num-tabular ${bg}`}>
            {cell(getValueForColumn(col), col)}
          </td>
        );
      })}
      <td className="text-right px-3 py-2 num-tabular font-medium">{cell(opts.totalValue, { kind: 'total' })}</td>
    </tr>
  );

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow={`P&L · ${active?.name || ''}`}
        title="Monthly profit & loss"
        description="Rolling 9-month view: recent actuals plus forecast months. If a past month is closed but the Indie Financials sheet hasn’t been refreshed yet, we insert an “actuals pending” column with the forecast baseline as a placeholder so the month is acknowledged. Actuals come from QuickBooks via the sheet — including the current month as it builds. Click a section in the forecast to expand line items."
        right={
          <div className="flex items-center gap-3 no-print">
            <CommentButton targetType="pnl" targetId={`pnl.net_income.${active?.id || ''}`} label="Net income (P&L)" scenarioId={active?.id} />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="pnl-print-btn"
              className="inline-flex items-center gap-2 border-2 border-indie-navy text-indie-navy px-3 py-1.5 text-xs uppercase tracking-wider font-semibold hover:bg-indie-navy hover:text-indie-cream"
            >
              <Printer size={14} /> PDF
            </button>
          </div>
        }
      />

      <DataFreshnessBanner kind="actuals" label="Indie Financials sheet" />

      <div className="bg-white border-t-4 border-indie-navy overflow-x-auto">
        <table data-testid="pnl-table" className="w-full text-xs">
          <thead>
            <tr className="bg-indie-navy text-indie-cream">
              <th className="text-left px-3 py-3 font-medium tracking-wider uppercase sticky left-0 bg-indie-navy min-w-[240px]">Line</th>
              {allColumns.map((c) => {
                let tag = 'FORECAST';
                let tagCls = 'text-indie-cream/50';
                let headerBg = '';
                if (c.kind === 'actual') { tag = 'ACTUAL'; tagCls = 'text-indie-yellow'; headerBg = 'bg-indie-evergreen/20'; }
                else if (c.kind === 'pending') { tag = 'ACTUALS PENDING'; tagCls = 'text-indie-yellow/85'; headerBg = 'bg-indie-cobalt/20'; }
                return (
                  <th
                    key={c.label}
                    className={`text-right px-3 py-3 font-medium tracking-wider uppercase whitespace-nowrap ${headerBg}`}
                    title={c.kind === 'pending' ? 'This month is in the past but the Indie Financials sheet hasn’t been refreshed yet. Numbers shown are forecast baseline as a placeholder.' : undefined}
                  >
                    {c.label}
                    <div className={`text-[9px] font-normal tracking-widest mt-0.5 ${tagCls}`}>{tag}</div>
                  </th>
                );
              })}
              <th className="text-right px-3 py-3 font-medium tracking-wider uppercase whitespace-nowrap">9-mo total</th>
            </tr>
          </thead>
          <tbody>
            {/* Income */}
            <tr className="bg-indie-cream/60"><td colSpan={allColumns.length + 2} className="px-3 py-2 label-editorial">Income</td></tr>
            {row('Trademark packages',
              (c) => lineValue(c, (m) => m.income.trademark, (a) => a.income?.trademark),
              { totalValue: lineSum((m) => m.income.trademark, (a) => a.income?.trademark) })}
            {row('Referral income',
              (c) => lineValue(c, (m) => m.income.referral, (a) => a.income?.referral),
              { totalValue: lineSum((m) => m.income.referral, (a) => a.income?.referral) })}
            {row('Refunds',
              (c) => lineValue(c, (m) => m.income.refunds, (a) => a.income?.refunds),
              { totalValue: lineSum((m) => m.income.refunds, (a) => a.income?.refunds) })}
            {row('Total income',
              (c) => c.kind === 'actual' ? c.actual.revenue : c.forecast.income.total,
              { bold: true, totalValue: grandTotals.revenue })}

            {/* Expenses — bucket totals light up on actuals (QBO sheet provides
                per-bucket totals). Drilling into a bucket shows forecast-side
                line items only since the QBO taxonomy uses different labels. */}
            {EXPENSE_SECTIONS.map((sec) => {
              const isOpen = expanded[sec.key];
              const firstMonth = months[0].expenses[sec.key].lines;
              const lineKeys = Object.keys(firstMonth);
              return (
                <React.Fragment key={sec.key}>
                  <tr className="bg-indie-cream/30 cursor-pointer hover:bg-indie-cream/60" onClick={() => toggle(sec.key)}>
                    <td className="px-3 py-2 label-editorial flex items-center gap-1">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {sec.label}
                    </td>
                    {allColumns.map((col) => {
                      const v = col.kind === 'actual'
                        ? col.actual.buckets?.[sec.key]
                        : col.forecast.expenses[sec.key].total;
                      const bg = col.kind === 'actual'
                        ? 'bg-indie-evergreen/[0.03] text-indie-evergreen'
                        : col.kind === 'pending'
                        ? 'bg-indie-cobalt/[0.04] text-indie-cobalt italic opacity-70'
                        : 'text-indie-ink/70';
                      return (
                        <td
                          key={`${sec.key}-${col.label}`}
                          className={`text-right px-3 py-2 num-tabular ${bg}`}
                        >
                          {v == null ? DASH : moneyFull(v)}
                        </td>
                      );
                    })}
                    <td className="text-right px-3 py-2 num-tabular font-medium">
                      {moneyFull(lineSum((m) => m.expenses[sec.key].total, (a) => a.buckets?.[sec.key]))}
                    </td>
                  </tr>
                  {isOpen && lineKeys.map((lk) => (
                    <tr key={lk} className="bg-white">
                      <td className="px-3 py-2 pl-10 text-indie-ink/80 capitalize">{lk.replace(/_/g, ' ')}</td>
                      {allColumns.map((col) => {
                        const bg = col.kind === 'actual'
                          ? 'bg-indie-evergreen/[0.03]'
                          : col.kind === 'pending'
                          ? 'bg-indie-cobalt/[0.04] italic opacity-70'
                          : '';
                        return (
                          <td
                            key={`${lk}-${col.label}`}
                            className={`text-right px-3 py-2 num-tabular text-indie-ink/70 ${bg}`}
                          >
                            {col.kind === 'actual' ? DASH : moneyFull(col.forecast.expenses[sec.key].lines[lk])}
                          </td>
                        );
                      })}
                      <td className="text-right px-3 py-2 num-tabular text-indie-ink/70">{moneyFull(sumLine((m) => m.expenses[sec.key].lines[lk]))}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}

            {row('Total expenses',
              (c) => c.kind === 'actual' ? c.actual.expenses : c.forecast.expenses.total,
              { bold: true, totalValue: grandTotals.expenses })}

            <tr className="bg-indie-navy text-indie-cream">
              <td className="px-3 py-3 font-medium uppercase tracking-wider text-[11px]">Net income</td>
              {allColumns.map((col) => {
                const val = col.kind === 'actual' ? col.actual.net_income : col.forecast.net_income;
                const cls = col.kind === 'actual'
                  ? 'text-indie-yellow'
                  : col.kind === 'pending'
                  ? 'text-indie-cream/70 italic'
                  : '';
                return (
                  <td key={`net-${col.label}`} className={`text-right px-3 py-3 num-tabular ${cls}`}>
                    {moneyFull(val)}
                  </td>
                );
              })}
              <td className="text-right px-3 py-3 num-tabular">{moneyFull(grandTotals.net_income)}</td>
            </tr>
            <tr className="bg-indie-navy text-indie-yellow">
              <td className="px-3 py-3 uppercase tracking-wider text-[11px]">Margin</td>
              {allColumns.map((col) => {
                const m = col.kind === 'actual'
                  ? (col.actual.revenue ? col.actual.net_income / col.actual.revenue : 0)
                  : col.forecast.margin;
                const cls = col.kind === 'pending' ? 'text-indie-yellow/60 italic' : '';
                return <td key={`margin-${col.label}`} className={`text-right px-3 py-3 num-tabular ${cls}`}>{pct(m)}</td>;
              })}
              <td className="text-right px-3 py-3 num-tabular">{pct(grandTotals.margin)}</td>
            </tr>
          </tbody>
        </table>
        <div className="px-4 py-2.5 text-[11px] text-indie-ink/50 border-t border-indie-navy/10 leading-relaxed">
          <div className="flex flex-wrap gap-4 mb-1.5">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-indie-evergreen/20" /> <span className="uppercase tracking-wider text-indie-evergreen">Actual</span> — QuickBooks / Indie Financials sheet</span>
            {pendingMonths.length > 0 && (
              <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-indie-cobalt/20" /> <span className="uppercase tracking-wider text-indie-cobalt">Actuals pending</span> — closed month, sheet not refreshed yet; forecast baseline shown as placeholder</span>
            )}
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-white border border-indie-navy/20" /> <span className="uppercase tracking-wider text-indie-ink/70">Forecast</span> — driven by Assumptions</span>
          </div>
          Bucket totals light up on actuals (Leadership / Marketing / Sales / Fulfillment / Overhead) — pulled from QuickBooks via the Indie Financials sheet. Drilling into a bucket shows forecast-side line items only because the QBO taxonomy uses different labels (Facebook Ads, Google Ads, etc.). Forecast columns update live when you change drivers in Assumptions. For contribution margin, variable/fixed classification, and ROI analysis, see the <a href="/margin-analysis" className="underline hover:text-indie-navy">Margin Analysis</a> page.
        </div>
      </div>
    </div>
  );
}
